'use client';
import { Aggregate } from '@shared/types';
import { PublicCourse, PublicEducator } from '@cademy-package/sdk-component-types';
import { FunctionComponent, useCallback, useMemo, useState } from 'react';
import ReviewBreakdown from '../../../components/ReviewBreakdown';
import styles from './styles.module.scss';
import ControlBar from './ReviewsSection/ControlBar';
import Alert from '../../../components/Alert';
import classNames from 'classnames';
import ReviewsModal from './ReviewsSection/ReviewsModal';
import Heading from './ReviewsSection/Heading';
import useCourseReviews from './ReviewsSection/ReviewsModal/Hooks/useCourseReviews';
import useEducatorReviews from './ReviewsSection/ReviewsModal/Hooks/useEducatorReviews';
import Review from '../../../components/Review';
import ShowAllButton from './ReviewsSection/ControlBar/ShowAllButton';

export type ReviewsSectionProps = {
    aggregate: Aggregate | undefined;
    educator: PublicEducator;
    course: PublicCourse;
};

const ReviewsSection: FunctionComponent<ReviewsSectionProps> = ({
    aggregate,
    educator,
    course,
}) => {
    const [courseReviews, fetchNextCourseReviews] = useCourseReviews(educator.slug, course.slug);
    const [educatorReviews] = useEducatorReviews(educator.slug);
    const [showAllReviewsModal, setShowAllReviewsModal] = useState(false);

    const openReviewsModal = useCallback(() => {
        setShowAllReviewsModal(true);
    }, []);

    const closeReviewsModal = useCallback(() => {
        setShowAllReviewsModal(false);
    }, []);

    const Controls = useMemo(() => {
        return function Controls({ className }: { className?: string }) {
            return (
                <ControlBar
                    className={classNames(styles.controlBar, className)}
                    count={aggregate?.count ?? 0}
                    educator={educator}
                    courseSlug={course.slug}
                    showAll={openReviewsModal}
                />
            );
        };
    }, [aggregate?.count, educator, course.slug, openReviewsModal]);

    const educatorReviewsSection = useMemo(() => {
        if (educatorReviews.length === 0) {
            return null;
        }

        return (
            <>
                <div className={styles.header}>
                    <h2>Provider Reviews</h2>
                </div>
                {educatorReviews.slice(0, 5).map((review, index) => (
                    <Review key={index} review={review} educator={educator} />
                ))}
            </>
        );
    }, [educatorReviews, educator]);

    if (!aggregate || aggregate.count === 0) {
        return (
            <div className={classNames(styles.courseReviewsSection, styles.reviewsPlaceholder)}>
                <div className={styles.header}>
                    <h2>Reviews</h2>
                </div>
                <Alert className={styles.alert} preset="info">
                    <span>
                        There are no reviews for <b>{course.name}</b> yet.
                    </span>
                    {educatorReviews.length > 0 ? (
                        <span>
                            Explore reviews for {educator.name} or be the first one to review this{' '}
                            {course.keyword || 'course'}
                        </span>
                    ) : (
                        <span>
                            Why not be the first one to review this {course.keyword || 'course'}?
                        </span>
                    )}
                </Alert>
                <Controls className={styles.minimal} />
                {educatorReviewsSection}
            </div>
        );
    }

    return (
        <div className={styles.courseReviewsSection}>
            <div className={styles.header}>
                <h2>Reviews</h2>
            </div>

            <div className={styles.ratingSummary}>
                <Heading count={aggregate.count} rating={aggregate.rating} />
                <Controls />
            </div>

            <Breakdown aggregate={aggregate} />

            <ReviewsModal
                visible={showAllReviewsModal}
                onClose={closeReviewsModal}
                educator={educator}
                reviews={courseReviews}
                fetchNext={fetchNextCourseReviews}
            />
            <div className={styles.showAllContainer}>
                <ShowAllButton count={aggregate.count} onClick={openReviewsModal} />
            </div>
        </div>
    );
};

type BreakdownProps = {
    aggregate: Aggregate;
};

const Breakdown: FunctionComponent<BreakdownProps> = ({ aggregate }) => {
    if (!aggregate.breakdown) {
        return null;
    }

    return <ReviewBreakdown breakdown={aggregate.breakdown} />;
};

export default ReviewsSection;
